import { useState } from 'react';
import {
  Typography,
  Box,
  Divider,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { useAnnouncementDismissals, useDeleteAnnouncement } from '@/hooks/useAnnouncements';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import {
  announcementTypeVariant,
  announcementTypeLabel,
  announcementStatusFromDates,
  pushAnnouncementStatusLabel,
  pushAnnouncementStatusVariant,
} from '@/components/ui/statusHelpers';
import { formatOrderDate } from '@liminal/utils/date';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import type { AdminAnnouncement, AdminAnnouncementDismissal } from '@/api/types';
import { AnnouncementFormDialog } from './AnnouncementFormDialog';
import { describeAudience } from './describeAudience';
import { fontSizes } from '@liminal/brand/scale';
import { getErrorMessage } from '@/errors/messages';

// One array drives the header cells and the DataTableBody column count.
const DISMISSAL_COLUMNS = [
  { column: 'userId', label: 'User ID' },
  { column: 'dismissedAt', label: 'Dismissed At' },
] as const;

/** Every dismissal for one announcement arrives together, so this is a true sort. */
const DISMISSAL_SORT_ACCESSORS = {
  userId: (dismissal: AdminAnnouncementDismissal) => dismissal.userId,
  dismissedAt: (dismissal: AdminAnnouncementDismissal) => dismissal.dismissedAt,
};

interface AnnouncementDetailDialogProps {
  open: boolean;
  onClose: () => void;
  announcement: AdminAnnouncement | null;
  /** Seed a new announcement from this one. Owned by the page, which holds the form. */
  onCopy?: (announcement: AdminAnnouncement) => void;
}

export function AnnouncementDetailDialog({ open, onClose, announcement, onCopy }: AnnouncementDetailDialogProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const deleteMutation = useDeleteAnnouncement();

  const {
    data: dismissalsData,
    isLoading: dismissalsLoading,
    isError: dismissalsError,
  } = useAnnouncementDismissals(announcement?.id ?? '');

  const { rows: dismissals, sort } = useTableSort(
    dismissalsData?.dismissals ?? [],
    DISMISSAL_SORT_ACCESSORS,
    'dismissedAt',
    'desc',
  );

  if (!announcement) return null;

  const status = announcementStatusFromDates(announcement.startsAt, announcement.expiresAt);
  const dismissalCount = dismissalsData?.total ?? 0;

  const handleDelete = async () => {
    setDeleteError(undefined);
    try {
      await deleteMutation.mutateAsync(announcement.id);
      setConfirmOpen(false);
      onClose();
    } catch (error) {
      // Was swallowed entirely, on the reasoning that mutation state would show
      // it — nothing rendered that state, so a failed delete looked identical
      // to a successful one and the row simply stayed put.
      setDeleteError(getErrorMessage(error));
    }
  };

  return (
    <>
      <Modal
        open={open && !editOpen}
        title={announcement.title}
        onClose={onClose}
        width="lg"
        actions={
          <>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="link" onClick={onClose}>Close</Button>
            {onCopy ? (
              <Button variant="secondary" startIcon="copy" onClick={() => onCopy(announcement)}>
                Copy
              </Button>
            ) : null}
            <Button variant="primary" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          </>
        }
      >
          {/* The receipt shape: label left, value right. The body is the one
              long field, so it stacks under its label instead. */}
          <Box sx={{ mb: 2 }}>
            <DetailRow
              label="Status"
              value={<StatusPill label={status.label} tone={status.variant} size="sm" />}
            />
            <DetailRow
              label="Type"
              value={
                <StatusPill
                  label={announcementTypeLabel(announcement.type)}
                  tone={announcementTypeVariant(announcement.type)}
                  size="sm"
                />
              }
            />
            <DetailRow label="Priority" value={announcement.priority ?? 0} />
            <DetailRow
              label="Audience"
              value={describeAudience(
                announcement.audienceKind,
                announcement.audienceDescription,
                announcement.audienceSize,
              )}
            />
            <DetailRow
              label="Push"
              value={
                announcement.pushAnnouncementId ? (
                  <StatusPill
                    label={pushAnnouncementStatusLabel(announcement.pushStatus)}
                    tone={pushAnnouncementStatusVariant(announcement.pushStatus)}
                    size="sm"
                  />
                ) : (
                  'None'
                )
              }
            />
            {announcement.actionLabel && (
              <DetailRow
                label="Action"
                value={
                  <>
                    {announcement.actionLabel}
                    {announcement.actionUrl && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({announcement.actionUrl})
                      </Typography>
                    )}
                  </>
                }
              />
            )}
            <DetailRow label="Starts At" value={formatOrderDate(announcement.startsAt)} />
            <DetailRow
              label="Expires At"
              value={announcement.expiresAt ? formatOrderDate(announcement.expiresAt) : 'Never'}
            />
            <DetailRow label="Created" value={formatOrderDate(announcement.createdAt)} />
            <DetailRow label="Updated" value={formatOrderDate(announcement.updatedAt)} />
            <Box sx={{ mt: 1.5 }}>
              <DetailRow
                layout="stacked"
                label="Body"
                value={<Box sx={{ whiteSpace: 'pre-wrap' }}>{announcement.body}</Box>}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Dismissals ({dismissalCount})
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {DISMISSAL_COLUMNS.map(({ column, label }) => (
                    <SortableTableCell key={column} column={column} sort={sort}>
                      {label}
                    </SortableTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <DataTableBody
                columns={DISMISSAL_COLUMNS.length}
                isLoading={dismissalsLoading}
                isError={dismissalsError}
                isEmpty={dismissals.length === 0}
                loadingRows={3}
                emptyMessage="No dismissals yet."
                errorMessage="Could not load the dismissals."
              >
                {dismissals.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <UserIdLink userId={d.userId} full onClick={onClose} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: fontSizes.xs }}>
                        {formatOrderDate(d.dismissedAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </DataTableBody>
            </Table>
          </TableContainer>
      </Modal>

      <AnnouncementFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        announcement={announcement}
      />

      {/* Was a `window.confirm`. A native dialog cannot carry the brand, cannot
          show a pending state while the delete is in flight, and cannot report
          a failure — so the previous version silently swallowed one. */}
      <ConfirmationDialog
        open={confirmOpen}
        title="Delete this announcement?"
        message={`"${announcement.title}" will be removed for everyone. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => void handleDelete()}
        onCancel={() => {
          setConfirmOpen(false);
          setDeleteError(undefined);
        }}
        data-testid="delete-announcement"
      >
        <FormErrorBanner error={deleteError} />
      </ConfirmationDialog>
    </>
  );
}
